<!-- =========================================================================================
  File Name: SelectedPointsPanel.vue
  Description: Panel to manage selected points for clustering
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="selected-points-panel">
		<vx-card :title="$t('Clustering.SelectedPoints')" class="mb-0">
			<!-- Close Button -->
			<template slot="actions">
				<feather-icon icon="XIcon" svgClasses="w-5 h-5 cursor-pointer" class="ml-2" @click="$emit('close')" />
			</template>

			<!-- Summary Statistics -->
			<div class="selection-summary mb-4">
				<div class="summary-card success">
					<feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5" />
					<div class="summary-content">
						<span class="summary-value">{{ selectedPoints.length }}</span>
						<span class="summary-label">{{ $t("Clustering.Selected") }}</span>
					</div>
				</div>

				<div class="summary-card info">
					<feather-icon icon="CircleIcon" svgClasses="w-5 h-5" />
					<div class="summary-content">
						<span class="summary-value">{{ availablePoints }}</span>
						<span class="summary-label">{{ $t("Clustering.Available") }}</span>
					</div>
				</div>

				<div class="summary-card warning">
					<feather-icon icon="LayersIcon" svgClasses="w-5 h-5" />
					<div class="summary-content">
						<span class="summary-value">{{ polygonsCount }}</span>
						<span class="summary-label">{{ $t("Clustering.Polygons") }}</span>
					</div>
				</div>
			</div>

			<!-- Quick Actions -->
			<div class="quick-actions mb-4">
				<vs-button
					color="success"
					type="border"
					icon-pack="feather"
					icon="icon-check-circle"
					size="small"
					class="w-full mb-2"
					@click="$emit('select-all')"
				>
					{{ $t("Clustering.SelectAll") }}
				</vs-button>

				<vs-button
					color="warning"
					type="border"
					icon-pack="feather"
					icon="icon-x-circle"
					size="small"
					class="w-full mb-2"
					:disabled="selectedPoints.length === 0"
					@click="$emit('deselect-all')"
				>
					{{ $t("Clustering.DeselectAll") }}
				</vs-button>

				<vs-button
					color="danger"
					type="border"
					icon-pack="feather"
					icon="icon-trash-2"
					size="small"
					class="w-full"
					:disabled="polygonsCount === 0"
					@click="$emit('clear-polygons')"
				>
					{{ $t("Clustering.ClearPolygons") }}
				</vs-button>
			</div>

			<vs-divider></vs-divider>

			<!-- Search -->
			<div class="search-section mb-3">
				<vs-input
					v-model="searchQuery"
					:placeholder="$t('Clustering.SearchPoints')"
					class="w-full"
					icon-pack="feather"
					icon="icon-search"
				/>
			</div>

			<!-- Selected Points List -->
			<div class="points-list">
				<div v-if="filteredPoints.length === 0" class="empty-state">
					<feather-icon icon="InboxIcon" svgClasses="w-12 h-12 text-grey" />
					<p class="text-grey mt-2">{{ $t("Clustering.NoPointsSelected") }}</p>
				</div>

				<transition-group v-else name="list" tag="div">
					<div
						v-for="point in paginatedPoints"
						:key="point.id"
						class="point-item"
						@click="$emit('point-click', point)"
					>
						<div class="point-info">
							<div class="point-icon">
								<feather-icon icon="MapPinIcon" svgClasses="w-4 h-4" />
							</div>
							<div class="point-details">
								<h6 class="point-name">{{ point.identification || point.id }}</h6>
								<p class="point-meta">
									<span v-if="point.address" class="meta-item">
										{{ truncateAddress(point.address) }}
									</span>
									<span v-if="point.demand" class="meta-item">
										<feather-icon icon="PackageIcon" svgClasses="w-3 h-3" />
										{{ point.demand }}
									</span>
								</p>
							</div>
						</div>
						<vs-button
							color="danger"
							type="flat"
							icon-pack="feather"
							icon="icon-x"
							size="small"
							@click.stop="$emit('remove-point', point)"
						></vs-button>
					</div>
				</transition-group>
			</div>

			<!-- Pagination -->
			<div v-if="totalPages > 1" class="pagination-section">
				<vs-pagination v-model="currentPage" :total="totalPages" :max="5" color="primary"></vs-pagination>
			</div>

			<!-- Footer Info -->
			<div class="footer-info">
				<small class="text-grey">
					{{
						$t("Clustering.ShowingItems", {
							from: paginationStart,
							to: paginationEnd,
							total: filteredPoints.length,
						})
					}}
				</small>
			</div>
		</vx-card>
	</div>
</template>

<script>
export default {
	name: "SelectedPointsPanel",
	props: {
		selectedPoints: {
			type: Array,
			default: () => [],
		},
		totalPoints: {
			type: Number,
			default: 0,
		},
		polygonsCount: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			searchQuery: "",
			currentPage: 1,
			itemsPerPage: 10,
		};
	},
	computed: {
		availablePoints() {
			return this.totalPoints - this.selectedPoints.length;
		},
		filteredPoints() {
			if (!this.searchQuery) {
				return this.selectedPoints;
			}

			const query = this.searchQuery.toLowerCase();
			return this.selectedPoints.filter((point) => {
				const identification = (point.identification || "").toLowerCase();
				const address = (point.address || "").toLowerCase();
				const id = String(point.id).toLowerCase();

				return identification.includes(query) || address.includes(query) || id.includes(query);
			});
		},
		totalPages() {
			return Math.ceil(this.filteredPoints.length / this.itemsPerPage);
		},
		paginatedPoints() {
			const start = (this.currentPage - 1) * this.itemsPerPage;
			const end = start + this.itemsPerPage;
			return this.filteredPoints.slice(start, end);
		},
		paginationStart() {
			return this.filteredPoints.length === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
		},
		paginationEnd() {
			const end = this.currentPage * this.itemsPerPage;
			return Math.min(end, this.filteredPoints.length);
		},
	},
	methods: {
		truncateAddress(address) {
			if (!address) return "";
			return address.length > 40 ? address.substring(0, 40) + "..." : address;
		},
	},
	watch: {
		searchQuery() {
			this.currentPage = 1;
		},
	},
};
</script>

<style lang="scss" scoped>
.selected-points-panel {
	position: absolute;
	bottom: 20px;
	left: 20px;
	width: 380px;
	max-height: calc(100vh - 160px);
	z-index: 100;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	border-radius: 8px;
	overflow: hidden;

	.vx-card {
		margin-bottom: 0;
		height: 100%;
		display: flex;
		flex-direction: column;
	}

	.selection-summary {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;

		.summary-card {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 12px;
			border-radius: 8px;
			background: #f8f9fa;

			&.success {
				background: rgba(76, 175, 80, 0.1);
				color: #4caf50;
			}

			&.info {
				background: rgba(33, 150, 243, 0.1);
				color: rgba(var(--vs-success), 1);
			}

			&.warning {
				background: rgba(255, 152, 0, 0.1);
				color: #ff9800;
			}

			.summary-content {
				display: flex;
				flex-direction: column;

				.summary-value {
					font-size: 1.25rem;
					font-weight: 700;
					line-height: 1;
				}

				.summary-label {
					font-size: 0.7rem;
					opacity: 0.8;
					margin-top: 2px;
				}
			}
		}
	}

	.quick-actions {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.search-section {
		margin-bottom: 12px;
	}

	.points-list {
		flex: 1;
		overflow-y: auto;
		min-height: 200px;
		max-height: 400px;
		margin-bottom: 12px;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-track {
			background: #f1f1f1;
			border-radius: 10px;
		}

		&::-webkit-scrollbar-thumb {
			background: #888;
			border-radius: 10px;

			&:hover {
				background: #555;
			}
		}

		.empty-state {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 40px 20px;
			text-align: center;
		}

		.point-item {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 12px;
			margin-bottom: 8px;
			border-radius: 8px;
			background: #f8f9fa;
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: #e3f2fd;
				transform: translateX(4px);
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
			}

			.point-info {
				display: flex;
				align-items: center;
				gap: 12px;
				flex: 1;

				.point-icon {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 32px;
					height: 32px;
					border-radius: 8px;
					background: #4caf50;
					color: white;
				}

				.point-details {
					flex: 1;

					.point-name {
						font-size: 0.9rem;
						font-weight: 600;
						margin: 0 0 4px 0;
						color: #333;
					}

					.point-meta {
						font-size: 0.75rem;
						color: #666;
						margin: 0;
						display: flex;
						flex-wrap: wrap;
						gap: 8px;

						.meta-item {
							display: inline-flex;
							align-items: center;
							gap: 4px;
						}
					}
				}
			}
		}
	}

	.pagination-section {
		display: flex;
		justify-content: center;
		margin-bottom: 12px;
	}

	.footer-info {
		text-align: center;
		padding: 8px 0;
		border-top: 1px solid #e0e0e0;
	}
}

// List transition
.list-enter-active,
.list-leave-active {
	transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
	opacity: 0;
	transform: translateX(-30px);
}

// Responsive
@media screen and (max-width: 768px) {
	.selected-points-panel {
		width: calc(100% - 40px);
		left: 20px;
		right: 20px;
		bottom: 10px;
		max-height: 50vh;
	}
}

@media screen and (max-width: 480px) {
	.selected-points-panel {
		width: calc(100% - 20px);
		left: 10px;
		right: 10px;

		.selection-summary {
			grid-template-columns: 1fr;
		}
	}
}
</style>
